import { useEffect, useRef, useState } from 'react'
import { Menu } from 'lucide-react'
import { LanguageButton } from './LanguageSelector'
import NotificationBellButton from './NotificationBellButton'
import TrustBar from './TrustBar'
import orellaWordmarkBlue from '../assets/logos/orella_46A8F9_logo.png'

/**
 * AppHeader — v242.128.0 (header band: Tonal Bokeh, option 2a)
 *
 * ONE header shell for Home, My Cases, My Visits and My Profile.
 *
 * Row 1 is identical on every screen and never re-renders its own appearance.
 * Row 2 is a tinted title band carrying the page's identity — greeting and
 * trust badges on Home, icon tile + title + optional meta chip elsewhere — and
 * ONLY that band's inner div cross-fades on navigation.
 *
 * ★★★ THE BAND TINT IS NOW THE CANONICAL ICE BLUE, AND THE PREVIOUS OVERRIDE WAS
 *     THE DEFECT. v242.76.0 shipped the band at #F4FAFF→#EAF4FE with a comment
 *     explaining that snapping the foot to #E9F8FF read "brighter and bluer than
 *     the drawing". That reasoning inverted the rule: #E9F8FF IS the palette's ice
 *     fill (278 uses in src against 21 for the ramp's #EDF6FC), the 2a spec names
 *     it explicitly, and a ramp in a state file is not the palette (D262 — the
 *     release lost to exactly this move on the MD panel). The drawing was right.
 *
 * ★★★ THE BOKEH ANCHORS ARE PERCENTAGES, AND THAT IS NOT A STYLE PREFERENCE.
 *     The 2a canvas lays its five circles out in absolute px on a 660px card.
 *     Carried over literally onto a 402pt phone the composition collapses: the
 *     22px accent at `right: 300` lands at x=80, directly on top of the 46px icon
 *     tile, and the two left-anchored circles bunch under the title. Rendered
 *     side by side at 402pt the literal version puts a visible dot on the tile
 *     and the remapped one reproduces the drawing. So the SIZES and the VERTICAL
 *     offsets are the spec's verbatim — they sit against a 46px tile and a 78px
 *     band, both of which are fixed — and only the HORIZONTAL anchors are
 *     expressed as a fraction of 660, which is what keeps the drawing intact
 *     across 390 / 402 / 430pt devices.
 *
 * ★★ THIS HEADER ABSORBS ITS OWN SAFE-AREA INSET, so every route using it must
 *    be listed in MobileLayout's fills-own-top-inset set — including /home.
 *    Miss that and a notched device stacks a white strip above a white bar.
 */

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const LIGHT_BLUE = '#A7D9FC'
const ICE_BLUE = '#E9F8FF'
const BAND_TOP = '#F7FCFF'
const DIVIDER = '#E2E8F0'

const FADE_MS = 150

// The 2a canvas card is 660px wide; horizontal anchors are carried as a fraction
// of it. Sizes and vertical offsets are absolute, exactly as specified.
const CANVAS_W = 660
const pct = (v) => ((v / CANVAS_W) * 100).toFixed(2) + '%'

// ★ Fills are rgba of Blue / Light Blue only — never a new hex, and never a hue
//   from outside the band's family (no purple, no amber, no green).
const BOKEH = [
  { top: -58, right: 40, size: 148, fill: 'rgba(167,217,252,0.45)' },
  { top: -22, right: 168, size: 66, fill: 'rgba(70,168,249,0.12)' },
  { bottom: -44, left: 190, size: 92, fill: 'rgba(167,217,252,0.30)' },
  { bottom: 8, left: 322, size: 34, fill: 'rgba(70,168,249,0.16)' },
  { top: 12, right: 300, size: 22, fill: 'rgba(167,217,252,0.60)' },
]

function Bokeh() {
  return BOKEH.map((b, i) => {
    const style = {
      position: 'absolute',
      width: b.size,
      height: b.size,
      borderRadius: 9999,
      background: b.fill,
    }
    if (b.top !== undefined) style.top = b.top
    if (b.bottom !== undefined) style.bottom = b.bottom
    if (b.left !== undefined) style.left = pct(b.left)
    if (b.right !== undefined) style.right = pct(b.right)
    return <div key={i} aria-hidden className="pointer-events-none" style={style} />
  })
}

export default function AppHeader({
  variant = 'page',       // 'home' | 'page'
  icon: Icon,             // lucide component for the page tile
  title,
  meta,                   // right-aligned chip, e.g. "1 case"
  greeting,               // home only
  greetingAdornment,      // home only — the waving hand, etc.
  onTrustTap,             // home only — TrustBar's bottom sheets
  onMenu,
  onLanguage,
  onNotifications,
}) {
  // Cross-fade the band contents whenever the page changes. Held in state so
  // the OUTGOING content stays mounted for the fade rather than snapping to the
  // new page's text at zero opacity and fading that in — which reads as a flash
  // of the wrong title.
  const [shown, setShown] = useState({ variant, Icon, title, meta, greeting, greetingAdornment })
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    setVisible(false)
    const t = setTimeout(() => {
      setShown({ variant, Icon, title, meta, greeting, greetingAdornment })
      setVisible(true)
    }, FADE_MS)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, title, meta, greeting])

  const ShownIcon = shown.Icon

  // PROD-1431 (R1431 L1): once the page scrolls, the pinned white row sits on white content, so it
  // draws a hairline under itself, as iOS does under a pinned bar. At rest there is none: the title
  // band's own top border separates the two rows.
  const headerRef = useRef(null)
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const sw = headerRef.current ? headerRef.current.closest('.scroll-wrapper') : null
    if (!sw) return undefined
    const onScroll = () => setScrolled(sw.scrollTop > 0)
    onScroll()
    sw.addEventListener('scroll', onScroll, { passive: true })
    return () => sw.removeEventListener('scroll', onScroll)
  }, [])

  // PROD-1431: row 1 (menu, logo, language, bell) is pinned at the top of the four tabs. It pays its own
  // top inset in white, so its padding is the status-bar band. A sticky element cannot leave its parent,
  // so the header is row 1 alone and the title band (row 2) is its next sibling and scrolls away, as
  // the large title does on iOS; the divider that closed the header now closes the band.
  return (
    <>
    <header
      ref={headerRef}
      className="sticky-inset-header flex-shrink-0"
      style={{ background: '#fff', paddingTop: 'env(safe-area-inset-top, 0px)', boxShadow: scrolled ? `0 1px 0 ${DIVIDER}` : 'none' }}
    >
      {/* row 1 — chrome. Never changes between pages. */}
      <div className="flex items-center justify-between" style={{ padding: '14px 20px 12px' }}>
        <div className="flex items-center" style={{ gap: 14 }}>
          <button onClick={onMenu} aria-label="Menu" className="p-1 -ml-1 border-none bg-transparent cursor-pointer">
            <Menu size={24} strokeWidth={2.4} style={{ color: '#2B4A64' }} />
          </button>
          <img src={orellaWordmarkBlue} alt="Orella" style={{ height: 27, width: 'auto', display: 'block' }} />
        </div>
        <div className="flex items-center" style={{ gap: 10 }}>
          {/* Existing components on purpose: LanguageButton already knows the
              active language's native name, and NotificationBellButton already
              carries the unread badge. A bare <Globe/> and <Bell/> per the spec
              would have silently dropped both. */}
          <LanguageButton onClick={onLanguage} />
          <NotificationBellButton onClick={onNotifications} />
        </div>
      </div>
    </header>

      {/* row 2 — tonal bokeh title band */}
      <div
        className="relative flex-shrink-0 overflow-hidden appheader-band"
        style={{
          borderTop: `1px solid ${ICE_BLUE}`,
          borderBottom: `1px solid ${DIVIDER}`,
          background: `linear-gradient(180deg, ${BAND_TOP} 0%, ${ICE_BLUE} 100%)`,
          padding: '14px 20px',
          minHeight: 78,
        }}
      >
        <Bokeh />

        <div
          className="relative appheader-band-inner motion-reduce:transition-none"
          style={{ transition: `opacity ${FADE_MS}ms ease-out`, opacity: visible ? 1 : 0 }}
        >
          {shown.variant === 'home' ? (
            <>
              <h1 className="flex items-center gap-2"
                  style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.025em', color: NAVY, margin: 0, lineHeight: 1.15 }}>
                {shown.greeting}
                {shown.greetingAdornment}
              </h1>
              <div style={{ marginTop: 10 }}>
                <TrustBar onTap={onTrustTap} />
              </div>
            </>
          ) : (
            <div className="flex items-center" style={{ gap: 14 }}>
              <span
                className="flex items-center justify-center flex-none"
                style={{
                  width: 46, height: 46, borderRadius: 13, background: '#fff',
                  border: `1.5px solid ${LIGHT_BLUE}`, boxShadow: '0 2px 6px rgba(21,53,79,0.06)',
                }}
              >
                {ShownIcon ? <ShownIcon size={23} strokeWidth={1.9} style={{ color: BLUE }} /> : null}
              </span>
              <h1 style={{ fontSize: 27, fontWeight: 800, letterSpacing: '-0.025em', color: NAVY, margin: 0, lineHeight: 1.15 }}>
                {shown.title}
              </h1>
              {shown.meta && (
                <span
                  className="flex items-center"
                  style={{
                    marginLeft: 'auto', height: 28, borderRadius: 999, background: '#fff',
                    border: `1.5px solid ${LIGHT_BLUE}`, padding: '0 12px',
                    fontSize: 13, fontWeight: 700, color: '#3F5F7C', whiteSpace: 'nowrap',
                  }}
                >
                  {shown.meta}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
